import { useState } from "react";
import { MapPin, Briefcase, ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CandidateForm } from "../components/Forms";
import { CtaSection, Kicker } from "../components/Sections";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { JOBS } from "../lib/data";

const FILTERS = ["All", "IT", "Non-IT"];
const SCHEMA = breadcrumbSchema([["Home", "/"], ["For Candidates", "/careers"]]);

export default function Careers() {
  const [filter, setFilter] = useState("All");
  const jobs = filter === "All" ? JOBS : JOBS.filter((j) => j.category === filter);

  return (
    <>
      <Seo
        title="Careers & Job Opportunities"
        description="Explore IT and Non-IT job opportunities across India and submit your profile to be matched with employers hiring for your skills and experience."
        keywords="IT Jobs India, Non-IT Jobs, Job Openings Chennai, Submit Resume, Recruitment Agency Jobs"
        schema={SCHEMA}
      />
      <PageHeader kicker="Careers / Jobs" title="Your Next Opportunity Starts Here"
        sub="Explore career opportunities and connect with employers looking for your skills and experience." />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="jobs-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <Kicker>Open Positions</Kicker>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-navy">Explore Jobs</h2>
          </div>
          <div className="inline-flex p-1 rounded-full border border-slate-200 bg-white w-fit">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${filter === f ? "bg-navy text-white" : "text-navy"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {jobs.map((job, i) => (
            <Reveal key={job.title + i} delay={(i % 2) * 0.06}>
              <div className="group bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-1 hover:border-sapphire/40 transition-[transform,border-color] duration-300" data-testid={`job-card-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-sapphire">{job.category} · {job.type}</span>
                    <h3 className="mt-2 text-xl font-bold text-navy">{job.title}</h3>
                  </div>
                  <ArrowUpRight className="text-slate-300 group-hover:text-sapphire transition-colors" size={22} />
                </div>
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-slate-400" />{job.location}</span>
                  <span className="inline-flex items-center gap-1.5"><Briefcase size={15} className="text-slate-400" />{job.exp}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-400">* Sample listings shown. New roles are added regularly — submit your profile below to be matched.</p>
      </section>

      <section className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="candidate-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center">
              <Kicker>Submit Your Profile</Kicker>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-navy">Get matched to the right role</h2>
              <p className="mt-4 max-w-xl mx-auto text-ink">Share your details and our recruiters will connect you with employers looking for your skills.</p>
            </div>
          </Reveal>
          <div className="mt-10"><CandidateForm /></div>
        </div>
      </section>
      <CtaSection />
    </>
  );
}
